import { iconX } from '../icons';

let escHandler: ((e: KeyboardEvent) => void) | null = null;
let trapHandler: ((e: KeyboardEvent) => void) | null = null;

export interface ShowModalOpts {
  wide?: boolean;
}

export function showModal(html: string, opts?: ShowModalOpts): void {
  const overlay = document.getElementById('modal-overlay');
  const modal = document.getElementById('modal');
  if (!overlay || !modal) return;

  modal.innerHTML = html;
  modal.classList.remove('modal-wide');
  if (opts?.wide) modal.classList.add('modal-wide');
  overlay.style.display = 'flex';
  overlay.classList.add('modal-fade-in');

  // Click outside to close
  overlay.onclick = (e) => { if (e.target === overlay) closeModal(); };

  // Escape key to close
  if (escHandler) document.removeEventListener('keydown', escHandler);
  escHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') closeModal();
  };
  document.addEventListener('keydown', escHandler);

  // Focus trap
  if (trapHandler) document.removeEventListener('keydown', trapHandler);
  trapHandler = (e: KeyboardEvent): void => {
    if (e.key !== 'Tab') return;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };
  document.addEventListener('keydown', trapHandler);

  // Auto-focus first input
  requestAnimationFrame(() => {
    const firstInput = modal.querySelector<HTMLElement>('input, select, textarea');
    if (firstInput) firstInput.focus();
  });
}

export function closeModal(): void {
  const overlay = document.getElementById('modal-overlay');
  const modal = document.getElementById('modal');
  if (overlay) {
    overlay.style.display = 'none';
    overlay.classList.remove('modal-fade-in');
  }
  if (modal) {
    modal.innerHTML = '';
    modal.classList.remove('modal-wide', 'modal-sss');
  }
  if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
  if (trapHandler) { document.removeEventListener('keydown', trapHandler); trapHandler = null; }
}

export function modalFrame(title: string, bodyHtml: string, footerHtml: string): string {
  return `
    <div class="modal-header">
      <h2>${esc(title)}</h2>
      <button class="btn-icon" id="modal-close">${iconX()}</button>
    </div>
    <div class="modal-body">${bodyHtml}</div>
    <div class="modal-footer">${footerHtml}</div>
  `;
}

export interface ConfirmOpts {
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
}

export function confirmModal(
  title: string,
  message: string,
  onConfirm: () => void,
  opts: ConfirmOpts = {}
): void {
  const confirmLabel = opts.confirmLabel ?? 'Confirm';
  const cancelLabel = opts.cancelLabel ?? 'Cancel';
  const variant = opts.variant ?? 'danger';
  showModal(modalFrame(title,
    `<p class="modal-confirm-msg">${esc(message)}</p>`,
    `<button class="btn btn-secondary" id="modal-cancel">${esc(cancelLabel)}</button>
     <button class="btn btn-${variant}" id="modal-confirm">${esc(confirmLabel)}</button>`
  ));
  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('modal-cancel')?.addEventListener('click', closeModal);
  document.getElementById('modal-confirm')?.addEventListener('click', () => { closeModal(); onConfirm(); });
}

export interface PromptOpts {
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  inputType?: 'text' | 'password' | 'email' | 'url';
  required?: boolean;
}

export function promptModal(
  title: string,
  message: string,
  onSubmit: (value: string) => void,
  opts: PromptOpts = {}
): void {
  const defaultValue = opts.defaultValue ?? '';
  const placeholder = opts.placeholder ?? '';
  const confirmLabel = opts.confirmLabel ?? 'OK';
  const cancelLabel = opts.cancelLabel ?? 'Cancel';
  const inputType = opts.inputType ?? 'text';
  const required = opts.required ?? false;
  const messageHtml = message ? `<p class="modal-prompt-msg">${esc(message)}</p>` : '';
  showModal(modalFrame(title,
    `${messageHtml}
     <input type="${inputType}" id="modal-prompt-input" class="modal-prompt-input"
            value="${esc(defaultValue)}" placeholder="${esc(placeholder)}" />`,
    `<button class="btn btn-secondary" id="modal-cancel">${esc(cancelLabel)}</button>
     <button class="btn btn-primary" id="modal-confirm">${esc(confirmLabel)}</button>`
  ));
  const input = document.getElementById('modal-prompt-input') as HTMLInputElement | null;
  const submit = (): void => {
    const value = input?.value ?? '';
    if (required && value.trim() === '') {
      input?.focus();
      input?.classList.add('input-error');
      return;
    }
    closeModal();
    onSubmit(value);
  };
  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('modal-cancel')?.addEventListener('click', closeModal);
  document.getElementById('modal-confirm')?.addEventListener('click', submit);
  input?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); submit(); }
  });
  requestAnimationFrame(() => { input?.select(); });
}

export interface AlertOpts {
  variant?: 'info' | 'warn' | 'error';
  okLabel?: string;
}

export function alertModal(
  title: string,
  message: string,
  onClose?: () => void,
  opts: AlertOpts = {}
): void {
  const okLabel = opts.okLabel ?? 'OK';
  showModal(modalFrame(title,
    `<p class="modal-confirm-msg">${esc(message)}</p>`,
    `<button class="btn btn-primary" id="modal-confirm">${esc(okLabel)}</button>`
  ));
  document.getElementById('modal-close')?.addEventListener('click', () => { closeModal(); onClose?.(); });
  document.getElementById('modal-confirm')?.addEventListener('click', () => { closeModal(); onClose?.(); });
}

function esc(str: string): string {
  const el = document.createElement('div');
  el.textContent = str;
  return el.innerHTML;
}
